import React, { Component } from 'react';
import Axios from 'axios';
import { injectStateService } from '@/shared/services/StateService';
import { FormattedMessage, injectIntl } from 'react-intl';
import SimplePanel from '@/shared/components/SimplePanel';
import api from '@/shared/api';
import config from '@/config';
import messages from '../reportingMessages';
import AbsenceCalendarFilters, { defaultFilters } from './AbsenceCalendarFilters';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import moment from 'moment';
import classNames from 'classnames';
import { mapResourceName } from '@/shared/services/ObjectDisplayService';
import { minutesToTimeSpan } from '@/shared/utils/adminpulse';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import RegistrationModal from '@/shared/components/registration/RegistrationModal';

import {
  absenceRegistrationOptions,
  canCreateRegistrationForUser,
} from '@/shared/components/registration/registrationHelpers';
import AddAbsenceDropdownButton from './AddAbsenceDropdownButton';
import { CanCreate } from '@/shared/components/security';
import AuthService from '@/shared/services/AuthService';

const SessionStorageKey = 'AbsenceCalendarParams';

const getClassForDayStatus = (dayStatus, isHoliday) => {
  if (!dayStatus || dayStatus === 0 || isHoliday) return '';
  switch (dayStatus) {
    case 1:
      return 'noworkday';
    case 2:
      return '';
    case 3:
      return 'halfDayMorning';
    case 4:
      return 'halfDayAfternoon';
  }
};
const getClassForAbsence = absence => {
  if (absence.startInMorning && absence.endInMorning) return 'absence left';
  if (absence.startInMorning && !absence.endInMorning) return 'absence full';
  if (!absence.startInMorning) return 'absence right';
};
const getResourceColor = (i, total) => {
  if (total < 1) total = 1;
  const color = (i * (360 / total)) % 360;
  return 'hsl( ' + color + ', 27%, 53% )';
};

class AbsenceCalendarContainer extends Component {
  constructor(props) {
    super(props);
    const sessionParams = JSON.parse(sessionStorage.getItem(SessionStorageKey)) || {};
    this.state = {
      report: null,
      showRegistrationModal: null,
      filters: {
        ...defaultFilters,
        ...sessionParams,
      },
    };
  }

  canSeeAbsenceDetails = () => {
    return AuthService.hasPermission({ functionality: 'RegistrationOthers', privilege: 'Read' });
  };

  filtersChanged = filters => {
    this.setState(
      state => ({
        filters: {
          ...state.filters,
          ...filters,
        },
        report: null,
      }),
      () => this.retrieve(),
    );
  };

  getParams = () => {
    const { filters } = this.state;
    return { ...filters, month: filters.month + 1 };
  };
  retrieve = () => {
    const { filters } = this.state;
    const { intl } = this.props;
    this.setState({ report: null });
    const params = this.getParams();

    const sessionParams = JSON.stringify(filters);
    sessionStorage.setItem(SessionStorageKey, sessionParams);

    if (typeof this.reportToken !== typeof undefined) {
      this.reportToken.cancel('Operation canceled due to new request.');
    }
    this.reportToken = Axios.CancelToken.source();

    return api.base
      .post(`${config.appUrls.reporting}/api/AbsenceCalendar`, params, {
        cancelToken: this.reportToken.token,
      })
      .then(({ data }) => {
        if (!data) {
          this.setState({
            report: {},
            dateColumns: [],
            resources: [],
            resourceLookup: {},
            hasHolidays: false,
          });
          return false;
        }
        const daysInMonth = moment
          .utc({ year: filters.year, month: filters.month, day: 1 })
          .daysInMonth();

        const dateColumns = Array.from({ length: daysInMonth }, (x, i) => ({
          key: i,
          date: moment.utc({ year: filters.year, month: filters.month, day: i + 1 }),
        }));
        const resourceCount = (data.resources || []).length;

        const resources = (data.resources || [])
          .map(x => ({ ...x, label: mapResourceName(x, intl) })) //Translate
          .sort(dynamicSort('label')) //Sort
          .map((x, i) => ({
            ...x,
            className: `color-${i}`,
            color: getResourceColor(i, resourceCount),
          })); //Add color index to object

        this.setState({
          report: data,
          dateColumns,
          resources,
          resourceLookup: resources.reduce((o, r) => {
            o[r.resourceId] = r;
            return o;
          }, {}),
          hasHolidays: data.isHoliday.some(x => x),
          // hasRemoteWorkingDays: data.rows.some(x => x?.remoteWorkingDays?.some(y => y?.isRemoteWorkingDay)),
          hasRemoteWorkingDays: data.rows.some(
            x =>
              x?.remoteWorkingDays &&
              Object.values(x.remoteWorkingDays).some(y => y?.isRemoteWorkingDay === true)
          ),
        });
        return true;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) {
          ToastrService.error();
        }
      });
  };

  componentDidMount() {
    this.retrieve();
  }

  // getFirstWorkday = () => {
  //   const { report, filters } = this.state;
  //   let date = moment.utc({ year: filters.year, month: filters.month, day: 1 });
  //   //Check if date is a holiday, add 1 day
  //   while (report && report.isHoliday[date.date() - 1]) {
  //     date = date.add(1, 'day');
  //   }
  //   if (date.isoWeekday() >= 6) {
  //     //Check if in weekend
  //     date = date.isoWeekday(8); //Set to next monday
  //     //Redo is holiday check
  //     while (report && report.isHoliday[date.date() - 1]) {
  //       date = date.add(1, 'day');
  //     }
  //   }
  //   return date;
  // };

  openRegistrationModal = (type, userId = null, date = null) => {
    this.setState(({ filters }) => ({
      showRegistrationModal: {
        registration: {
          type,
          userId: userId ?? AuthService.getUserId(),
          date: date ? moment.utc(date).toDate() : moment.utc().toDate(),
        },
        registrationOptions: absenceRegistrationOptions,
      },
    }));
  };

  createExcelReport = () => {
    const { intl } = this.props;

    const reportParams = this.getParams();
    const languageCode = intl.locale;
    const params = {
      language: languageCode,
      signalRConnectionId: SignalRService.getConnectionId(),
      report: 9,
      parameters: JSON.stringify(reportParams),
    };

    return api.base
      .post(`${config.appUrls.reporting}/api/export`, params)
      .then(() => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  renderEmployeeRow = (row, intl) => {
    const { report, dateColumns, resourceLookup } = this.state;
    return (
      <tbody key={row.employeeId}>
        <tr>
          <th>{row.name}</th>
          {dateColumns.map((c, ci) => {
            const absences = row.cells[ci] || null;

            return (
              <td
                key={c.key}
                className={classNames(
                  getClassForDayStatus(row.dayStatus[ci], report.isHoliday[ci]),
                  {
                    holiday: report.isHoliday[ci],
                  },
                )}
              >
                <div className="absence-table">
                  {row.remoteWorkingDays[ci] && (
                    <div className="remote-working-container" >
                    {row.remoteWorkingDays[ci].isRemoteWorkingDay && (
                      <i className="fa fa-home" title={intl.formatMessage(messages.WorkingFromHome)}></i>
                    )}
                  </div>
                  )}
                  {absences && absences.length > 0 && (

                    absences.map((a, ai) => {
                      const resource = this.canSeeAbsenceDetails()
                        ? resourceLookup[a.resourceId]
                        : undefined;
                      return (
                        <div className="absence-row" key={`${a.day};${a.resourceId}`}>
                          <div
                            style={{ backgroundColor: !!resource ? resource.color : 'gray' }}
                            className={getClassForAbsence(a)}
                            title={`${a.timeFrom} → ${a.timeTo} - ${!!resource ? resource.label : ''
                              } (${minutesToTimeSpan(a.duration)})`}
                          >
                            &nbsp;
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </td>
            );
          })}
        </tr>
      </tbody>
    );
  };

  render() {
    const {
      report,
      filters,
      dateColumns,
      resources,
      hasHolidays,
      showRegistrationModal,
      hasRemoteWorkingDays,
    } = this.state;
    const { intl } = this.props;

    return (
      <div className="absence-calendar">
        <SimplePanel
          article="15769589036572"
          title={intl.formatMessage(messages.AbsenceCalendar)}
          buttons={
            <CanCreate on="RegistrationOwn">
              <AddAbsenceDropdownButton
                onOpenRegistrationModal={this.openRegistrationModal}
                userId={filters.userId}
                intl={intl}
              />
            </CanCreate>
          }
        >
          <div className="info-block container-fluid">
            <AbsenceCalendarFilters
              filters={filters}
              onFilterChange={this.filtersChanged}
              canSeeAbsenceDetails={this.canSeeAbsenceDetails()}
            />
          </div>
        </SimplePanel>

        {report && report.rows && report.rows.length > 0 && (
          <>
            <table className="table table-grid">
              <thead>
                <tr>
                  <th>
                    <FormattedMessage {...messages.Employee} />
                  </th>
                  {dateColumns.map((x, xi) => (
                    <th
                      key={x.key}
                      className={classNames('text-center', { holiday: report.isHoliday[xi] })}
                    >
                      {moment(x.date).format('dd')}
                      <br />
                      {moment(x.date).format('DD')}
                    </th>
                  ))}
                </tr>
              </thead>
              {report.rows.map(row => this.renderEmployeeRow(row, intl))}
            </table>
            {resources && resources.length > 0 && (
              <div className="text-center absence-type-legend">
                {this.canSeeAbsenceDetails() &&
                  resources.map(r => (
                    <span className="margin10" key={r.id}>
                      <span className="label" style={{ backgroundColor: r.color }}>
                        &nbsp;
                      </span>
                      &nbsp;
                      <span>{r.label}</span>
                    </span>
                  ))}
                {hasHolidays ? (
                  <span className="margin10 holiday">
                    <span className="label">&nbsp;</span>
                    &nbsp;
                    <FormattedMessage {...messages.Holiday} />
                  </span>
                ) : null}
              </div>
            )}
          </>
        )}
        {report && (!report.rows || !report.rows.length) && (
          <div>
            <FormattedMessage {...messages.NoDataForReport} />
          </div>
        )}
        {!report && <LoadingAnimation />}
        {showRegistrationModal && (
          <RegistrationModal
            {...showRegistrationModal}
            show
            onSave={() =>
              this.setState({
                showRegistrationModal: null,
              })
            }
            onClose={() =>
              this.setState({
                showRegistrationModal: null,
              })
            }
          />
        )}
      </div>
    );
  }
}

export default injectStateService(injectIntl(AbsenceCalendarContainer));
